import { useFlag } from '@automata/utils/hooks';
import Stack from '@mui/material/Stack';
import { useCallback, useEffect } from 'react';

import { TaskDependenciesTable } from './components/TaskDependenciesTable';
import { ConnectorizableFileParam } from './components/TaskFields/ConnectorizableFileParam';
import { TaskMenu } from './components/TaskMenu';
import { ScriptSelect } from './ScriptSelect';
import { SidebarWrap } from './SidebarWrap';
import { deleteNodesEdges } from '../../features/actions';
import {
  deselectNodesEdges,
  updateNodesEdgesSelection,
} from '../../features/graph/graphSlice';
import { selectDataDependencyEdges } from '../../features/graph/selectors';
import {
  selectCommentsById,
  selectSelectedWorkcellId,
} from '../../features/workflow/selectors';
import {
  setComment,
  updateTaskProperties,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  CodeTaskProperties,
  CodeTaskPropertiesForm,
  PropertiesType,
} from '../../types';
import { isFileUploadEnabled } from '../../utils/dataModes';
import { DataMode } from '../../zodSchemas/taskProperties';
import Chip from '../ChipWFB';
import { Icon } from '../customIcons/misc';
import { SemicontrolledInput } from '../SemicontrolledInput';

interface CodeTaskSidebarProps {
  selectedNodeId: string;
  properties: CodeTaskProperties;
}

const INPUT_FIELD_NAME = 'initial_state_csv';

export const CodeTaskSidebar = ({
  selectedNodeId,
  properties,
}: CodeTaskSidebarProps) => {
  const dispatch = useAppDispatch();
  const comment = useAppSelector(selectCommentsById)[selectedNodeId];
  const dataDependencyEdges = useAppSelector(selectDataDependencyEdges);
  const workcellId = useAppSelector(selectSelectedWorkcellId);
  const apiBatchingEnabled = useFlag('fe.api_batching');

  const handleUpdateTaskFields = useCallback(
    (formUpdate: Partial<CodeTaskPropertiesForm>) => {
      const { nickname } = formUpdate;
      const nicknameUpdate =
        typeof nickname === 'string'
          ? { nickname: nickname.trim() === '' ? '-' : nickname.trim() }
          : {};

      dispatch(
        updateTaskProperties({
          ...formUpdate,
          ...nicknameUpdate,
          id: selectedNodeId,
          propertiesType: PropertiesType.Code,
        })
      );
    },
    [selectedNodeId, dispatch]
  );

  const handleCloseSidebar = useCallback(() => {
    dispatch(deselectNodesEdges());
  }, [dispatch]);

  const handleSetComment = useCallback(
    (comment: string) => {
      dispatch(setComment({ id: selectedNodeId, comment }));
    },
    [selectedNodeId, dispatch]
  );

  const handleUpdateScript = useCallback(
    (scriptName: string | null) => {
      const inputDataMode = isFileUploadEnabled(scriptName, workcellId)
        ? DataMode.Manual
        : DataMode.Connector;

      handleUpdateTaskFields({
        scriptName: scriptName ?? '',
        inputDataMode,
        taskParameters: null,
        taskAppliedParameters: null,
      });

      const edge = dataDependencyEdges.find(
        (e) =>
          e.target === properties.id &&
          e.targetHandle?.includes(INPUT_FIELD_NAME)
      );
      if (edge) {
        dispatch(deleteNodesEdges({ edgeIds: [edge.id] }));
        dispatch(
          updateNodesEdgesSelection({
            nodes: { selected: [properties.id], deselected: [] },
          })
        );
      }
    },
    [
      dataDependencyEdges,
      properties.id,
      workcellId,
      dispatch,
      handleUpdateTaskFields,
    ]
  );

  useEffect(() => {
    // set settings with 'fe.api_batching' flag off
    if (!apiBatchingEnabled && properties.inputDataMode === DataMode.Manual) {
      handleUpdateTaskFields({ inputDataMode: DataMode.Connector });
    }
  }, [apiBatchingEnabled, properties.inputDataMode, handleUpdateTaskFields]);

  return (
    <SidebarWrap
      comment={comment}
      handleCloseSidebar={handleCloseSidebar}
      handleSetComment={handleSetComment}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          pb={1}
        >
          {/* minHeight is a quick way keep visual consistency between this component and BaseTaskSettings */}
          <Stack direction="row" alignItems="center" gap={2} minHeight={50}>
            <Icon color="neutral" fontSize="large" iconType="processdata" />
            <Chip label="Process Data" />
          </Stack>
          <TaskMenu taskId={properties.id} />
        </Stack>

        <SemicontrolledInput
          TextFieldProps={{ label: 'Task name' }}
          value={properties.nickname ?? ''}
          handleSaveValue={(value) =>
            handleUpdateTaskFields({ nickname: value })
          }
          maxLength={25}
        />

        <ScriptSelect
          label="Operation"
          value={properties.scriptName}
          onChange={handleUpdateScript}
        />

        {apiBatchingEnabled &&
          isFileUploadEnabled(properties.scriptName, workcellId) && (
            <ConnectorizableFileParam
              inputParameterName={INPUT_FIELD_NAME}
              label="Initial state csv"
              mode={properties.inputDataMode}
              required={true}
              taskProperties={properties}
              updateTaskFields={handleUpdateTaskFields}
            />
          )}

        <TaskDependenciesTable taskId={properties.id} />
      </Stack>
    </SidebarWrap>
  );
};
